<template>
    <central-modal
        :header="props.definition ? `${props.definition.title}` : 'Definition'"
        v-model:visible="isVisible"
        :size="{ width: '80vw' }"
        hide-header
        @hide="close"
    >
        <div v-if="props.definition && isSparkDefinition" class="definition-container spark-definition-container">
            <div class="header-container">
                <div>
                    <div>
                        <span class="title">{{ props.definition.title }}</span>
                    </div>
                    <div class="alternate-headers" v-if="props.definition.description">
                        {{ props.definition.description }}
                    </div>
                </div>
            </div>
            <div v-if="canViewSparkDefinition" class="spark-definition-body" v-html="sanitizedSparkDefinitionBody" />
            <div v-else class="spark-definition-no-access">
                This definition is available to subscribed Spark users only.
            </div>
        </div>
        <div v-else-if="props.definition" class="definition-container">
            <div class="header-container">
                <div>
                    <div>
                        <span class="title">{{ props.definition.title }}</span>
                        <span v-if="props.definition.units.length && props.definition.units[0]">
                            ({{ props.definition.units.join(', ') }})
                        </span>
                    </div>
                    <div class="alternate-headers" v-if="props.definition.otherTitles.length > 0 && props.definition.otherTitles[0] !== ''">
                        Also known as
                        <strong>{{ props.definition.otherTitles.join(' / ') }}</strong>
                    </div>
                </div>
                <div v-if="props.definition.isEuropean" class="icon-flag" v-tooltip="'This series includes data for multiple European countries'">
                    <country-flag iso-code="eu" />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="content">
                    <div v-for="section in props.definition.sections" class="info" :key="`section-${section.header}`">
                        <div class="info-title">{{ section.header }}</div>
                        <ul v-if="section.bulletPoints.length > 0 && section.bulletPoints[0].length > 0">
                            <li
                                v-for="bullet, index in section.bulletPoints"
                                v-html="sanitizeRichTextHtml(bullet)"
                                class="bullet"
                                :key="`${section.header}-bullet-${index}`"
                                @click="selectLinkFromHtml"
                                @keydown.enter="selectLinkFromHtml"
                            />
                        </ul>
                    </div>
                    <div class="info" v-if="!props.definition.hideFromFrontEnd">
                        <div class="info-title">Enact API</div>
                        <span v-if="userIsLoggedIn">
                            (See
                            <a class="link" :href="ENACT_API_URL" target="_blank" rel="noopener noreferrer">here</a>
                            for more details)
                        </span>
                        <ul>
                            <template v-if="userIsLoggedIn">
                                <li class="bullet">
                                    Series ID: <strong>{{ props.definition.id }}</strong>
                                </li>
                                <li v-if="selectedCountry" class="bullet">
                                    Country ID: <strong>{{ selectedCountry.id }}</strong>
                                </li>
                                <li v-if="shouldShowOptionId" class="bullet">
                                    Option ID:
                                    <template v-if="hasCompleteOptionSelection">
                                        <strong>{{ optionIdDisplay }}</strong>
                                    </template>
                                    <em v-else>Please select an option below</em>
                                </li>
                            </template>
                            <li v-else class="bullet">
                                Requires an Enact license with API access.
                            </li>
                        </ul>
                        <div class="dropdowns">
                            <country-selector
                                v-if="shouldShowCountrySelector"
                                :model-value="selectedCountry"
                                allow-internal-country
                                :allowed-countries="countrySelectorAllowedCountries"
                                @update:model-value="selectCountry"
                            />
                            <plant-selector
                                v-if="props.definition.isPlant"
                                :selected-plant-ids="selectedPlant ? selectedPlant.id : null"
                                :country-id="selectedCountry?.id"
                                @update:model-value-single="updateSelectedPlant"
                            />
                            <template v-if="hasOptions && selectedCountry">
                                <primevue-select
                                    v-for="(optionGroup, index) in optionGroups"
                                    :key="`definition-option-group-${index}`"
                                    :model-value="selectedOptions[index]"
                                    allow-empty
                                    open-direction="above"
                                    option-label="name"
                                    placeholder="Select an option"
                                    track-by="id"
                                    :options="optionGroup"
                                    :show-labels="false"
                                    @update:model-value="option => selectOption(option, index)"
                                />
                            </template>
                        </div>
                    </div>
                    <div class="info" v-if="props.definition.hasHistoryOfForecast">
                        Enact tracks how this forecast changes over time.
                        You can view this by adding a Forecast Evolution chart on your Dashboard, going to the Forecast Evolution page, or using our API.
                    </div>
                </div>
                <div class="chart-container">
                    <div v-if="!props.definition.hideFromFrontEnd" class="chart">
                        <div class="view-chart" v-if="!chartCanRender">
                            {{ chartPlaceholderText }}
                        </div>
                        <single-chart-module
                            v-else
                            chart-config-id="singleChart"
                            is-modal
                            :series-id="props.definition.id"
                            :option-id="hasCompleteOptionSelection ? combinedOptionId : undefined"
                            :country-id="selectedCountry?.id"
                            :blocked-actions="DEFINITION_BLOCKED_ACTIONS"
                            :title="props.definition.title"
                            :to-date="toDate"
                            :from-date="fromDate"
                            display-options-top
                            @update:chart-date="updateChartDate"
                        />

                    </div>
                    <div class="related">
                        <div class="related-section" v-if="props.definition.relatedDynamicSeries.length > 0">
                            <span class="related-title">Related Enact Series:</span>
                            <span class="related-text">
                                <span v-for="series, seriesIndex in props.definition.relatedDynamicSeries" :key="`relatedDynamicSeries-${seriesIndex}`">
                                    <button class="related-text-link" type="button" @click="selectRelatedDefinition(series.id)">{{ series.name }}</button>
                                    <span v-if="seriesIndex !== props.definition.relatedDynamicSeries.length - 1">, </span>
                                </span>
                            </span>
                        </div>
                        <div class="related-section" v-if="props.definition.relatedDynamicCharts.length > 0">
                            <span class="related-title">Related Enact Charts:</span>
                            <span class="related-text">
                                <span v-for="charts, chartsIndex in props.definition.relatedDynamicCharts" :key="`relatedDynamicCharts-${chartsIndex}`">
                                    <span class="related-text-link">{{ charts }}</span>
                                    <span v-if="chartsIndex != props.definition.relatedDynamicCharts.length - 1">, </span>
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="sources" class="source">
                <strong>Data Source:</strong>
                <template v-for="sourceInfo, sourceIndex in sources" :key="`countrySource-${sourceIndex}`">
                    <a class="link" :href="sourceInfo.sourceUrl" target="_blank" rel="noopener noreferrer">
                        {{ sourceInfo.SourceEnum }}
                    </a>
                    <template v-if="sourceIndex !== sources.length - 1"> /</template>
                </template>
            </div>
        </div>
        <div v-else class="no-definition-container">
            <p>The Enact team are working on bringing you this definition soon.</p>
            <p>
                For help with this series in the meantime, please email
                <a :href="`mailto:${ENACT_EMAIL}`">{{ ENACT_EMAIL }}</a>
                , and we'll be happy to help.
            </p>
        </div>
        <definition-modal
            v-if="newDefinition !== null"
            :definition="newDefinition"
            @close:modal="newDefinition = null"
        />
    </central-modal>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import CentralModal from '@/components/common/CentralModal.vue';
import PrimevueSelect from 'primevue/select';
import { IDataSource, IDefinition } from '@/interfaces/powerMarkets/static/IDefinitionSearchInfo.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { IPlant } from '@/interfaces/powerMarkets/plant/IPlant.ts';
import { authState } from '@lcp/http-auth';
import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';
import { dateFormatToAnyFormat } from '@/utils/dateUtils.ts';
import { getDatesFromConfig } from '@/helpers/datePickerMethod.ts';
import SingleChartModule from '@/components/chart/SingleChartModule.vue';
import dayjs, { type Dayjs } from 'dayjs';
import { useDefinitionStore } from '@/stores/powerMarkets/definitionStore.ts';
import { sanitizeRichTextHtml } from '@/utils/htmlUtils.ts';
import { DEFINITION_BLOCKED_ACTIONS } from '@/interfaces/actions/enact/IModuleActions.ts';
import { useInitialCountrySelection } from '@/composables/powerMarkets/useInitialCountrySelection.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import { storeToRefs } from 'pinia';
import CountryFlag from '../shared/CountryFlag.vue';
import CountrySelector from '../selectors/CountrySelector.vue';
import PlantSelector from '../selectors/PlantSelector.vue';

const ENACT_API_URL = 'https://api.lcpdelta.com/';
const ENACT_EMAIL = 'enact.helpdesk@lcp.uk.com';

const definitionStore = useDefinitionStore();
const { resolveInitialCountryId } = useInitialCountrySelection();
const { isMobileView } = storeToRefs(useUIStore());

const props = defineProps<{ definition?: IDefinition; country?: IIdName }>();
const emit = defineEmits<{ 'close:modal': [] }>();

const selectedCountry = ref<IIdName | undefined>(props.country);
const selectedPlant = ref<IPlant | null>(null);
const selectedOptions = ref<Array<IIdName | null>>([]);
const newDefinition = ref<IDefinition | null | undefined>(null);

const isVisible = ref<boolean>(false);
const isSparkDefinition = computed<boolean>(() => props.definition?.source === 'spark');
const canViewSparkDefinition = computed<boolean>(() => props.definition?.hasAccess !== false);
const sanitizedSparkDefinitionBody = computed<string>(() => sanitizeRichTextHtml(props.definition?.bodyHtml || props.definition?.description));

const chartDate = ref<IDateObject>(dateFormatToAnyFormat('', 'IDateObject'));
const userIsLoggedIn = computed<boolean>(() => authState.value.loggedIn);

const resolvedChartDate = computed<{ from: Dayjs | null; to: Dayjs | null }>(() => getDatesFromConfig(chartDate.value));
const fromDate = computed<string>(() => (resolvedChartDate.value.from ?? dayjs().startOf('day')).toISOString());
const toDate = computed<string>(() => (resolvedChartDate.value.to ?? dayjs().endOf('day')).toISOString());

const optionGroups = computed<Array<Array<IIdName>>>(() => {
    if (!selectedCountry.value || !props.definition) return [];
    return props.definition.options[selectedCountry.value.id] ?? [];
});

const hasOptions = computed<boolean>(() => {
    if (!selectedCountry.value || !props.definition) return false;
    return optionGroups.value.some(optionGroup => optionGroup.length > 0);
});

const shouldShowCountrySelector = computed<boolean>(() => {
    if (!props.definition) return false;
    return Object.keys(props.definition.options).length > 1;
});

const countrySelectorAllowedCountries = computed<Array<string>>(() => {
    if (!props.definition) return [];
    return Object.keys(props.definition.options);
});

const shouldShowOptionId = computed<boolean>(() => {
    if (!props.definition) return false;
    return hasOptions.value || props.definition.isPlant;
});

const selectedOptionParts = computed<Array<IIdName>>(() => {
    const optionParts: Array<IIdName> = [];

    if (props.definition?.isPlant && selectedPlant.value?.id) {
        optionParts.push({
            id: selectedPlant.value.id,
            name: selectedPlant.value.name,
        });
    }

    selectedOptions.value.forEach(option => {
        if (option?.id) optionParts.push(option);
    });

    return optionParts;
});

const requiredOptionPartCount = computed<number>(() => {
    if (!props.definition) return 0;
    return (props.definition.isPlant ? 1 : 0) + (hasOptions.value ? optionGroups.value.length : 0);
});

const hasCompleteOptionSelection = computed<boolean>(() => (
    shouldShowOptionId.value
    && selectedOptionParts.value.length === requiredOptionPartCount.value
    && selectedOptionParts.value.every(option => !!option.id)
));

const combinedOptionId = computed<string>(() => selectedOptionParts.value.map(option => option.id).join('*'));

const optionIdDisplay = computed<string>(() => {
    if (selectedOptionParts.value.length === 1) return selectedOptionParts.value[0].id;
    return `[${selectedOptionParts.value.map(option => `"${option.id}"`).join(', ')}]`;
});

const chartCanRender = computed<boolean>(() => {
    if (!props.definition) return false;
    if (!props.definition.isPlant) return true;
    return !!selectedPlant.value && (!hasOptions.value || hasCompleteOptionSelection.value);
});

const chartPlaceholderText = computed<string>(() => {
    const directionSubString = isMobileView.value ? 'above' : 'on the left';
    if (!props.definition?.isPlant) return '';
    if (!selectedPlant.value) return `To view chart, please pick a plant ${directionSubString}`;
    return `To view chart, please pick an option ${directionSubString}`;
});

const sources = computed<Array<IDataSource> | null>(() => {
    if (!props.definition) return null;

    const definitionSources = [
        ...(props.definition.dataSources ?? []),
        ...Object.values(props.definition.dataSourcesByCountry ?? {}).flat(),
    ];

    return definitionSources.length ? definitionSources : null;
});

function updateChartDate (date: IDateObject): void {
    chartDate.value = date;
}

function selectCountry (country: IIdName): void {
    selectedCountry.value = country;
    selectedPlant.value = null;
    selectedOptions.value = [];
}

function selectOption (option: IIdName | null, index: number): void {
    selectedOptions.value[index] = option;
}

function updateSelectedPlant (plant: IPlant): void {
    selectedPlant.value = plant;
}

async function selectRelatedDefinition (definitionId: string): Promise<void> {
    newDefinition.value = await definitionStore.getDefinitionById(definitionId);
}

function selectLinkFromHtml (event: MouseEvent | KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('info_text_definition')) {
        const definitionId = target.getAttribute('data');

        if (definitionId) {
            selectRelatedDefinition(definitionId);
        }
    }
}

function close (): void {
    selectedCountry.value = undefined;
    selectedPlant.value = null;
    selectedOptions.value = [];

    setTimeout(() => {
        emit('close:modal');
    }, 100);
}

function getInitialCountry (definition?: IDefinition): IIdName | undefined {
    if (!definition) return props.country;

    const initialCountryId = resolveInitialCountryId({
        availableCountryIds: Object.keys(definition.options),
        explicitCountryId: props.country?.id,
    });

    if (!initialCountryId) return props.country;
    return { id: initialCountryId, name: initialCountryId };
}

function initialise (): void {
    isVisible.value = props.definition !== null;
    selectedCountry.value = getInitialCountry(props.definition);
    selectedPlant.value = null;
    selectedOptions.value = [];
}

initialise();

</script>

<style lang="scss" scoped>
.info {
    font-size: var(--smallFontSize);

    .bullet {
        line-height: 20px;
    }
}

.no-definition-container {
    padding: var(--defaultSpacing);
}

.definition-container {
    display: grid;
    grid-template-rows: auto 1fr;
}

.spark-definition-container {
    .spark-definition-body {
        padding: var(--largeSpacing);
        line-height: 1.75;
    }

    .spark-definition-no-access {
        padding: var(--largeSpacing);
        color: var(--neutral700);
    }
}

.header-container {
    display: flex;
    gap: var(--defaultSpacing);
    padding: var(--defaultSpacing);
    border-bottom: solid 1px var(--neutral300);

    .title {
        font-size: var(--largeFontSize);
        font-weight: var(--boldFontWeight);
    }

    .alternate-headers {
        font-size: var(--defaultFontSize);
        margin-top: 5px;
    }

    .icon-flag {
        border: 1px solid white;
        height: 50px;
        width: 50px;
        position: relative;
    }
}

.content-wrapper {
    display: grid;
    gap: var(--extraLargeSpacing);
    grid-template-columns: 1fr 1fr;
    padding: var(--defaultSpacing);

    @include responsive(sm) {
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr;
    }

    .content {
        display: flex;
        flex-direction: column;
        gap: var(--defaultSpacing);
    }

    .info {
        .info-title {
            margin: var(--defaultSpacing) 0;
            font-size: var(--defaultFontSize);
            font-weight: var(--boldFontWeight);
        }

        .dropdowns {
            margin-top: 10px;
            display: flex;
            gap: 5px;
        }
    }

    .chart-container {
        overflow: hidden;
        .chart {
            height: 400px;
            border: solid 1px var(--neutral300);
            border-radius: var(--borderRadiusOuter);
        }

        .view-chart {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
        }
    }

    .related {
        margin-top: 10px;

        .related-section {
            .related-title {
                font-weight: var(--boldFontWeight);
                padding-right: 5px;
            }

            .related-text {
                .related-text-link {
                    cursor: pointer;
                }
            }
        }
    }
}

.source {
    padding: 0 var(--defaultSpacing) var(--defaultSpacing);
    font-size: var(--smallFontSize);
    font-weight: var(--boldFontWeight);
    text-align: left;

    .link {
        margin-left: 5px;
    }
}

</style>

<style lang="scss">
.info .bullet .info_text_definition {
    color: var(--accentColour);
    font-weight: var(--boldFontWeight);
    cursor: pointer;
}

.spark-definition-body {
    h2, h3, h4 {
        margin-top: var(--largeSpacing);
        margin-bottom: var(--defaultSpacing);
    }

    p {
        margin-bottom: var(--defaultSpacing);
    }

    ul, ol {
        padding-left: var(--extraLargeSpacing);
        margin-bottom: var(--defaultSpacing);
    }

    a {
        color: var(--accentColour);
    }
}

</style>
